import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Mail, Phone, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchContacts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/contacts`, { withCredentials: true });
      setContacts(data);
    } catch (error) {
      console.error('Contacts fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div data-testid="admin-contacts-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-primary mb-2" data-testid="contacts-title">Contact Submissions</h1>
        <p className="text-text_muted">View messages from potential clients</p>
      </div>

      {loading ? (
        <div className="text-center py-20" data-testid="loading">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text_muted">Loading contacts...</p>
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-sm border border-border" data-testid="empty">
          <p className="text-xl text-text_muted">No contact submissions yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-border overflow-hidden">
          <table className="w-full" data-testid="contacts-table">
            <thead className="bg-surface border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact, index) => (
                <tr key={contact.id} className="border-b border-border last:border-b-0" data-testid={`contact-row-${index}`}>
                  <td className="px-6 py-4 text-text_main font-medium">{contact.name}</td>
                  <td className="px-6 py-4 text-text_muted">{contact.email}</td>
                  <td className="px-6 py-4 text-text_muted">{contact.phone || '-'}</td>
                  <td className="px-6 py-4 text-text_muted text-sm">{formatDate(contact.created_at)}</td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => setSelectedContact(contact)}
                      className="px-4 py-2 bg-accent/10 text-accent rounded hover:bg-accent/20 transition-colors"
                      data-testid={`view-contact-${index}`}
                    >
                      View Message
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Contact Detail Modal */}
      {selectedContact && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedContact(null)}
          data-testid="contact-modal"
        >
          <div
            className="bg-white rounded-sm max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-border">
              <h2 className="text-2xl font-bold text-primary" data-testid="modal-title">Contact Details</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-semibold text-primary">Name</label>
                <p className="text-text_main mt-1" data-testid="modal-name">{selectedContact.name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-primary flex items-center">
                    <Mail size={16} className="mr-1 text-accent" />
                    Email
                  </label>
                  <p className="text-text_muted mt-1" data-testid="modal-email">
                    <a href={`mailto:${selectedContact.email}`} className="hover:text-accent transition-colors">
                      {selectedContact.email}
                    </a>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-primary flex items-center">
                    <Phone size={16} className="mr-1 text-accent" />
                    Phone
                  </label>
                  <p className="text-text_muted mt-1" data-testid="modal-phone">
                    {selectedContact.phone || 'Not provided'}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-primary flex items-center">
                  <Calendar size={16} className="mr-1 text-accent" />
                  Date Submitted
                </label>
                <p className="text-text_muted mt-1" data-testid="modal-date">{formatDate(selectedContact.created_at)}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-primary">Message</label>
                <p className="text-text_muted mt-2 whitespace-pre-wrap leading-relaxed" data-testid="modal-message">
                  {selectedContact.message}
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-border flex justify-end">
              <button
                onClick={() => setSelectedContact(null)}
                className="px-6 py-3 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all"
                data-testid="modal-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContacts;
